import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

export type UserRole = 'admin' | 'rattare' | 'deltagare'

export interface Profile {
  id: string
  email: string
  display_name: string
  /** Publikt namn. Null tills deltagaren valt ett — se NicknameGate. */
  nickname: string | null
  role: UserRole
}

interface AuthState {
  session: Session | null
  profile: Profile | null
  loading: boolean
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  async function loadProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, display_name, nickname, role')
      .eq('id', userId)
      .maybeSingle()

    // Ingen profil är inte ett fel vid första inloggningen — triggern på
    // auth.users skapar den, men replikeringen kan ligga någon tick efter.
    if (error) console.error('Kunde inte läsa profilen:', error.message)
    setProfile((data as Profile) ?? null)
  }

  useEffect(() => {
    let active = true

    // Realtime-socketen måste bära användarens JWT. Utan den ansluter den som
    // anon, och eftersom varje policy är skriven `to authenticated` filtreras
    // samtliga händelser bort — prenumerationen ser ut att lyckas men levererar
    // ingenting. Verifierat: utan detta kom noll händelser fram.
    function syncRealtimeAuth(next: Session | null) {
      void supabase.realtime.setAuth(next?.access_token ?? null)
    }

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return
      setSession(data.session)
      syncRealtimeAuth(data.session)
      if (data.session) await loadProfile(data.session.user.id)
      setLoading(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, next) => {
      if (!active) return
      setSession(next)
      syncRealtimeAuth(next)
      if (next) await loadProfile(next.user.id)
      else setProfile(null)
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      session,
      profile,
      loading,
      signOut: async () => {
        await supabase.auth.signOut()
      },
      refreshProfile: async () => {
        if (session) await loadProfile(session.user.id)
      },
    }),
    [session, profile, loading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth måste användas inuti AuthProvider')
  return ctx
}
